import React, {useEffect, useState} from 'react';
import {
    DetailsListLayoutMode,
    DirectionalHint,
    FontIcon,
    IColumn,
    IStackStyles,
    IStackTokens,
    ITooltipHostStyles,
    ScrollablePane,
    ScrollbarVisibility,
    SelectionMode,
    ShimmeredDetailsList,
    Stack,
    Sticky,
    StickyPositionType,
    Text,
    TooltipDelay,
    TooltipHost,
} from '@fluentui/react';
import {useTranslation} from 'react-i18next';
import {mergeStyles, mergeStyleSets} from '@fluentui/react/lib/Styling';
import {getProject, getTimeZone} from '../../../shared/util';
import {Selection} from '@fluentui/react/lib/DetailsList';
import '../../../shared/kanban/kanban.css';
import moment from 'moment';
import DragBox from '../../../shared/dnd/dragBox';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {filterMyDayPlanningResourceListPaneData} from '../../../reducers/myDayPlanningReducer';
import {AcceptType, GridView} from '../../../types/myDayPlanning';
import _ from 'lodash';
import usePrevious from '../../../hooks/usePrevious';
import {messageService} from '../../../services/messageService';
import {saveDayPlanningStateAttr} from "../../../reducers/dayPlanningReducer";
import { iconColors } from '../../../../theme';

const iconClass = mergeStyles({
    fontSize: 14,
    height: 14,
    width: 14,
    margin: '0 5px',
});

export const classNames = mergeStyleSets({
    wrapper: {
        height: 'calc(100vh - 25vh)!important',
        position: 'relative',
    },
    filter: {
        paddingBottom: 20,
        maxWidth: 100,
    },
    header: {
        margin: 0,
    },
    row: {
        display: 'inline-block',
    },
    deepSkyBlue: [{color: 'deepskyblue'}, iconClass],
    red: [{color: 'red'}, iconClass],
    green: [{color: 'green'}, iconClass],
    orange: [{color: 'orange'}, iconClass],
});

const wrapStackTokens: IStackTokens = {childrenGap: 2};

const hostStyles: Partial<ITooltipHostStyles> = {root: {display: 'inline-block'}};

const stackStyles: IStackStyles = {
    root: {
        padding: 0
    },
};

const ListPaneComponent = (props: { horizontalReSizer: any }) => {
    const {t} = useTranslation()
    const dispatch = useDispatch();
    const store = useStore();

    const [selection, setSelection] = useState<Selection | undefined>();

    const workerButtonId = 'worker';
    const toolButtonId = 'tool';

    const columns = [
        {
            key: 'column1',
            name: t('date'),
            fieldName: 'day',
            minWidth: 100,
            maxWidth: 125,
            isResizable: true,
        },
        {
            key: 'column2',
            name: t('workers'),
            fieldName: 'workers',
            minWidth: 100,
            maxWidth: 1050,
            isResizable: true,
        },
        {
            key: 'column3',
            name: t('vehicle'),
            fieldName: 'vehicle',
            minWidth: 100,
            maxWidth: 500,
            isResizable: true,
        },
    ];

    const toolColumns = [
        {
            key: 'column1',
            name: t('date'),
            fieldName: 'day',
            minWidth: 100,
            maxWidth: 200,
            isResizable: true,
        },
        {
            key: 'column2',
            name: t('tools'),
            fieldName: 'tool',
            minWidth: 100,
            maxWidth: 200,
            isResizable: true,
        }
    ];

    const {
        resourceList,
        view,
        selectedDate,
        selectedWeek,
        isResourceDataLoaded,
        mydPlanResourceType,
        reloadBottomListPane,
        isDataLoaded
    } = useSelector(
        (state: any) => state.mydPlan
    );

    const {userInfo} = useSelector(
        (state: any) => state.uprince
    );

    const {buDropDown, selectedCu, selectedBu} = useSelector(
        (state: any) => state.projectDPlan
    );
    const createNewPmolSuccess = useSelector((state: any) => state?.dplan?.createNewPmolSuccess);

    const prevView: any = usePrevious<any>(view);
    const prevDate: any = usePrevious<any>(selectedDate);
    // const prevStartDate: any = usePrevious<any>(selectedWeek.startDate);

    useEffect(() => {
        // dispatch(filterMyDayPlanningResourceListPaneData(getFilterDate(true)));

        const subscription = messageService.getMessage().subscribe((data: any) => {
            if (data) {

            }
        });

        return () => {
            subscription.unsubscribe();
            // console.log('willUnmount')
        }
    }, []);

    // useEffect(() => {
    //     if (reloadBottomListPane) {
    //         dispatch(filterMyDayPlanningResourceListPaneData(getFilterDate()));
    //
    //         return () => {
    //             dispatch(saveMyDayPlanningStateAttr('reloadBottomListPane', false));
    //         }
    //     }
    // }, [reloadBottomListPane]);

    useEffect(() => {
        if (prevView && prevView !== view) {
            dispatch(filterMyDayPlanningResourceListPaneData(getFilterDate()));
        }
    }, [view]);

    useEffect(() => {
        if (prevDate && moment(prevDate).format('YYYY-MM-DD') !== moment(selectedDate).format('YYYY-MM-DD')) {
            dispatch(filterMyDayPlanningResourceListPaneData(getFilterDate()));
        }
    }, [selectedDate]);

    useEffect(() => {
        if (!_.isEmpty(selectedWeek)) {
            // console.log('selectedWeek.startDate> ', selectedWeek.startDate);
            // console.log('prevStartDate> ', prevStartDate);
            dispatch(filterMyDayPlanningResourceListPaneData(getFilterDate()));
        }
    }, [selectedWeek]);

    useEffect(() => {
        if (resourceList) {
            getResourceList(resourceList);
        }
    }, [resourceList]);

    useEffect(() => {
        if (selectedCu && selectedBu && !getProject()) {
            dispatch(filterMyDayPlanningResourceListPaneData(getFilterDate()));
        }
    }, [selectedCu, selectedBu]);

    useEffect(() => {
        if (createNewPmolSuccess) {
            dispatch(filterMyDayPlanningResourceListPaneData(getFilterDate()));
        }

        return () => {
            dispatch(saveDayPlanningStateAttr('createNewPmolSuccess', false));
        }
    }, [createNewPmolSuccess]);

    const getFilterDate = (didMount?: boolean) => {
        const currentDate = moment();

        if (didMount) {
            return {
                startDate: moment(store.getState()?.mydPlan?.selectedDate).format('YYYY-MM-DD'),
                endDate: moment(store.getState()?.mydPlan?.selectedDate).clone().format('YYYY-MM-DD'),
                localDate: moment(currentDate).format('YYYY-MM-DD'),
                offSet: getTimeZone(),
                type: GridView.DAY,
                contractingUnit: getProject() ? store.getState()?.projectDPlan?.selectedCu?.sequenceCode : userInfo ? userInfo?.contractingUnit?.sequnceCode : [],
                buId: getProject() ? store.getState()?.projectDPlan?.selectedBu : buDropDown ? buDropDown[0]?.key : null,
            }
        } else {
            return {
                startDate: store.getState()?.mydPlan?.view === GridView.DAY
                    ? moment(store.getState()?.mydPlan?.selectedDate).format('YYYY-MM-DD')
                    : store.getState()?.mydPlan?.selectedWeek.startDate ? moment(store.getState()?.mydPlan?.selectedWeek.startDate).format('YYYY-MM-DD') : moment(currentDate).startOf('isoWeek').format('YYYY-MM-DD'),
                endDate: store.getState()?.mydPlan?.view === GridView.DAY
                    ? moment(store.getState()?.mydPlan?.selectedDate).format('YYYY-MM-DD')
                    : store.getState()?.mydPlan?.selectedWeek.endDate ? moment(store.getState()?.mydPlan?.selectedWeek.endDate).format('YYYY-MM-DD') : moment(currentDate).clone().endOf('isoWeek').format('YYYY-MM-DD'),
                localDate: moment(currentDate).format('YYYY-MM-DD'),
                offSet: getTimeZone(),
                type: store.getState()?.mydPlan?.view,
                contractingUnit: store.getState()?.projectDPlan?.selectedCu ? store.getState()?.projectDPlan?.selectedCu?.sequenceCode : userInfo ? userInfo?.contractingUnit?.sequnceCode : [],
                buId: store.getState()?.projectDPlan?.selectedBu ? store.getState()?.projectDPlan?.selectedBu : null,
            }
        }
    };

    const getResourceList = (resourceList: any) => {
        return resourceList;
    };

    const onClickItem = (item: any) => {

    }

    const formatwithoutTime = (date: moment.MomentInput) => {
        const formatted = moment.utc(date).local().format('dddd MMM DD');
        return formatted === 'Invalid date' ? '' : formatted;
    };

    const getWorkerIconColor = (precentage: any): string => {
        const getDouble = parseFloat(precentage);

        if (0.0 <= getDouble && getDouble <= 80) {
            return classNames.red;
        } else if (80 < getDouble && getDouble <= 110) {
            return classNames.green;
        } else if (110 < getDouble) {
            return classNames.orange;
        }

        return classNames.deepSkyBlue
    };

    const resourceListColumn = (item: any, index?: number, column?: IColumn) => {
        if (column && column.fieldName && (column.fieldName === 'workers')) {
            const fieldContent = item[column.fieldName];
            return fieldContent ? <span style={{paddingLeft: 9}} onClick={() => {
            }}>
                <Stack horizontal wrap styles={stackStyles} tokens={wrapStackTokens}>
                    {fieldContent.map((worker: any) => {
                        return <div key={Math.random()}>
                            <TooltipHost
                                delay={TooltipDelay.medium}
                                id={worker?.projectList}
                                directionalHint={DirectionalHint.topCenter}
                                styles={hostStyles}
                                content={worker?.projectList?.length ? (
                                  worker.projectList?.map((project: any) => <li>{project}</li>)
                                ) : (
                                  <span>{t('noAssignedProjects')}</span>
                                )}
                            >
                                <DragBox
                                    // disabled={worker.absence ? true : !getProject()}
                                    type={AcceptType.TEAM}
                                    onClick={(item: any) => onClickItem(item)}
                                    item={{...worker, day: item['day']}}
                                    name={worker?.name}
                                    icon={!worker?.absence
                                        ? <FontIcon aria-label='Worker' iconName='Contact'
                                                    className={getWorkerIconColor(worker?.precentage)}/>
                                        :
                                      <FontIcon aria-label='Sick' iconName='WarningSolid' className={classNames.red}
                                                style={{ color: iconColors.warningIconColor }} />
                                    }
                                />
                            </TooltipHost>
                        </div>
                    })}
                </Stack>
            </span> : <span style={{paddingLeft: 9}}>-</span>;
        } else if (column && column.fieldName && (column.fieldName === 'vehicle')) {
            const fieldContent = item[column.fieldName];
            return fieldContent ? <span style={{paddingLeft: 9}} onClick={() => {
            }}>
                <Stack horizontal wrap styles={stackStyles} tokens={wrapStackTokens}>
                    {fieldContent.map((vehicle: any) => {
                        return <div key={Math.random()}>
                            <DragBox
                                // disabled={!getProject()}
                                type={AcceptType.VEHICLE}
                                onClick={(item: any) => onClickItem(item)}
                                item={{...vehicle, day: item['day']}}
                                name={vehicle?.title}
                                icon={<FontIcon aria-label="Compass" iconName="DeliveryTruck"
                                                className={classNames.deepSkyBlue}/>}
                            />
                        </div>
                    })}
                </Stack>
            </span> : <span style={{paddingLeft: 9}}>-</span>;
        } else if (column && column.fieldName && (column.fieldName === 'day')) {
            const fieldContent = item[column.fieldName];
            return fieldContent ? <span style={{paddingLeft: 9}} onClick={() => {
            }}>
                {formatwithoutTime(fieldContent)}
            </span> : <span style={{paddingLeft: 9}}>-</span>;
        } else if (column && column.fieldName) {
            const fieldContent = item[column.fieldName];
            return fieldContent ? <span style={{paddingLeft: 9}} onClick={() => {
            }}>{fieldContent}</span> : <span style={{paddingLeft: 9}}>-</span>;
        }

        return <span style={{paddingLeft: 9}}>-</span>;
    };

    const resourceToolsListColumn = (item: any, index?: number, column?: IColumn) => {
        if (column && column.fieldName && (column.fieldName === 'tool')) {
            const fieldContent = item[column.fieldName];
            return fieldContent ? <span style={{paddingLeft: 9}} onClick={() => {
            }}>
                <Stack horizontal wrap styles={stackStyles} tokens={wrapStackTokens}>
                    {fieldContent.map((worker: any) => {
                        return <div key={Math.random() + "tool"}>
                            <DragBox
                                // disabled={worker.absence ? true : !getProject()}
                                type={AcceptType.TOOL}
                                onClick={(item: any) => onClickItem(item)}
                                item={{...worker, day: item['day']}}
                                name={worker?.title}
                                icon={<FontIcon aria-label="Compass" iconName="DeveloperTools"
                                                className={worker.absence ? classNames.red : classNames.deepSkyBlue}/>}
                            />
                        </div>
                    })}
                </Stack>
            </span> : <span style={{paddingLeft: 9}}>-</span>;
        } else if (column && column.fieldName && (column.fieldName === 'day')) {
            const fieldContent = item[column.fieldName];
            return fieldContent ? <span style={{paddingLeft: 9}} onClick={() => {
            }}>
                {formatwithoutTime(fieldContent)}
            </span> : <span style={{paddingLeft: 9}}>-</span>;
        } else if (column && column.fieldName) {
            const fieldContent = item[column.fieldName];
            return fieldContent ? <span style={{paddingLeft: 9}} onClick={() => {
            }}>{fieldContent}</span> : <span style={{paddingLeft: 9}}>-</span>;
        }

        return <span style={{paddingLeft: 9}}>-</span>;
    };

    const displayMessage = () => {
        return (
            <div style={{paddingTop: 36, paddingLeft: 20}}>
                <Text>{t('loadMsg')}</Text>
            </div>
        );
    };
    return (
        <div className={`wrapper-holder`}
             style={{position: 'relative', height: screen.height - (props.horizontalReSizer + 365), overflow: 'auto'}}>
            {/* <pre>  {JSON.stringify(resourceList, 0, 2)}</pre> */}
            <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                <ShimmeredDetailsList
                    items={resourceList ?? []}
                    columns={mydPlanResourceType === workerButtonId ? columns : toolColumns}
                    setKey="set"
                    enableShimmer={!isResourceDataLoaded}
                    layoutMode={mydPlanResourceType === workerButtonId ? DetailsListLayoutMode.fixedColumns : DetailsListLayoutMode.justified}
                    selectionPreservedOnEmptyClick={true}
                    checkboxVisibility={1}
                    selection={selection}
                    selectionMode={SelectionMode.none}
                    ariaLabelForSelectionColumn="Toggle selection"
                    ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                    checkButtonAriaLabel="Row checkbox"
                    onRenderDetailsHeader={(headerProps, defaultRender) => {
                        if (defaultRender !== undefined) {
                            return (
                                <Sticky
                                    stickyPosition={StickyPositionType.Header}
                                    isScrollSynced={true}
                                    stickyBackgroundColor="transparent"
                                >
                                    <div>{defaultRender(headerProps)}</div>
                                </Sticky>
                            );
                        } else {
                            return <span></span>;
                        }
                    }}
                    onRenderItemColumn={(
                        item: any,
                        index?: number,
                        column?: IColumn,
                    ) =>
                        mydPlanResourceType === workerButtonId ? resourceListColumn(item, index, column) : resourceToolsListColumn(item, index, column)
                    }
                />
                {isDataLoaded && displayMessage()}
            </ScrollablePane>
        </div>
    );
};

export default ListPaneComponent;
